import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Volume2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedPhrase() {
  const { shareId } = useParams();
  const [phrase, setPhrase] = useState(null);
  const [missing, setMissing] = useState(false);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/shared/phrases/${shareId}`)
      .then(({ data }) => setPhrase(data))
      .catch(() => setMissing(true));
  }, [shareId]);

  const play = async (slow) => {
    const key = slow ? "slow" : "normal";
    if (playing) return;
    setPlaying(key);
    const audio = new Audio(`${API}/shared/phrases/${shareId}/speech?slow=${slow}`);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => setPlaying(null);
    try {
      await audio.play();
    } catch {
      setPlaying(null);
    }
  };

  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="shared-phrase-missing">
        <p className="text-sm text-ink-soft">This phrase link is no longer available.</p>
        <Link to="/" className="mt-6 inline-flex text-sm text-brand hover:underline">Go to ChatMiLu</Link>
      </div>
    );
  }

  if (!phrase) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24" data-testid="shared-phrase-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-5 py-16 sm:px-8" data-testid="shared-phrase-page">
      <Link to="/">
        <img src="/logo.png" alt="ChatMiLu" className="h-10 w-auto" />
      </Link>

      <p className="mt-10 text-xs uppercase tracking-[0.2em] text-ink-soft" data-testid="shared-phrase-by">
        {phrase.shared_by} shared a phrase with you
      </p>

      <div className="mt-4 rounded-3xl border border-white/10 bg-panel p-7">
        <h1
          dir="auto"
          data-testid="shared-phrase-text"
          className={`whitespace-pre-wrap ${
            phrase.text.length > 180
              ? "text-lg font-medium leading-relaxed"
              : "font-display text-3xl font-extrabold tracking-tighter sm:text-4xl"
          }`}
        >
          {phrase.text}
        </h1>
        {phrase.translation && (
          <p dir="auto" className="mt-3 whitespace-pre-wrap text-sm text-ink-soft" data-testid="shared-phrase-translation">
            {phrase.translation}
          </p>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            data-testid="shared-phrase-speak"
            onClick={() => play(false)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
          >
            <Volume2 className="h-4 w-4" />
            {playing === "normal" ? "Playing…" : "Hear it"}
          </button>
          <button
            data-testid="shared-phrase-slow"
            onClick={() => play(true)}
            className="inline-flex min-h-[44px] items-center rounded-full border border-white/15 px-5 text-xs font-bold tracking-wide text-ink-soft transition-colors hover:border-brand/50 hover:text-brand active:scale-95"
          >
            {playing === "slow" ? "…" : "SLOW"}
          </button>
        </div>
      </div>

      <Link
        to="/signup"
        data-testid="shared-phrase-cta"
        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline"
      >
        Speak 50 languages with ChatMiLu <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
